/**
 * RecordFilingModal — captures acknowledgement number, filing date,
 * and refund/demand outcome when an assessment is marked as Filed.
 */

import { useState } from 'react'
import { X, CheckCircle2 } from 'lucide-react'

export interface RecordFilingFormValues {
  acknowledgeNumber: string
  filedDate: string
  refundAmount?: number
  demandAmount?: number
}

interface RecordFilingModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (values: RecordFilingFormValues) => Promise<void>
  initialAcknowledgeNumber?: string
  initialFiledDate?: string
  initialRefundAmount?: number
  initialDemandAmount?: number
}

export default function RecordFilingModal({
  isOpen,
  onClose,
  onSubmit,
  initialAcknowledgeNumber = '',
  initialFiledDate = new Date().toISOString().split('T')[0],
  initialRefundAmount,
  initialDemandAmount,
}: RecordFilingModalProps) {
  const [ackNo, setAckNo] = useState(initialAcknowledgeNumber)
  const [filedDate, setFiledDate] = useState(initialFiledDate)
  const [outcomeType, setOutcomeType] = useState<'refund' | 'demand' | 'nil'>(
    initialRefundAmount ? 'refund' : initialDemandAmount ? 'demand' : 'nil'
  )
  const [outcomeAmount, setOutcomeAmount] = useState<string>(
    initialRefundAmount
      ? String(initialRefundAmount)
      : initialDemandAmount
        ? String(initialDemandAmount)
        : ''
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!ackNo.trim()) {
      setError('Please provide an acknowledgement number')
      return
    }

    if (!filedDate) {
      setError('Please select a filing date')
      return
    }

    const amountNum = outcomeAmount ? Number(outcomeAmount) : 0
    if (outcomeType !== 'nil' && (!outcomeAmount || isNaN(amountNum) || amountNum < 0)) {
      setError('Please enter a valid amount')
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit({
        acknowledgeNumber: ackNo.trim(),
        filedDate,
        refundAmount: outcomeType === 'refund' ? amountNum : undefined,
        demandAmount: outcomeType === 'demand' ? amountNum : undefined,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record filing')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md bg-surface-primary rounded-xl shadow-2xl border border-surface-overlay overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-overlay">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
            <h3 className="font-semibold text-text-primary">Record ITR Filing</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-xs btn-square"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-700 bg-red-50 dark:bg-red-950/40 dark:text-red-300 rounded-lg border border-red-200 dark:border-red-900">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Acknowledgement Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={ackNo}
              onChange={(e) => setAckNo(e.target.value)}
              placeholder="e.g. 123456789012345"
              className="input input-bordered w-full font-mono text-sm"
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Filing Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={filedDate}
              onChange={(e) => setFiledDate(e.target.value)}
              className="input input-bordered w-full text-sm"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              Filing Outcome
            </label>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button
                type="button"
                onClick={() => setOutcomeType('nil')}
                className={`py-2 px-3 text-xs font-medium rounded-lg border text-center transition ${
                  outcomeType === 'nil'
                    ? 'bg-primary-100 text-primary-700 border-primary-500 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'border-surface-overlay text-text-secondary hover:bg-surface-overlay'
                }`}
              >
                Nil / Neutral
              </button>
              <button
                type="button"
                onClick={() => setOutcomeType('refund')}
                className={`py-2 px-3 text-xs font-medium rounded-lg border text-center transition ${
                  outcomeType === 'refund'
                    ? 'bg-green-100 text-green-700 border-green-500 dark:bg-green-900/30 dark:text-green-300'
                    : 'border-surface-overlay text-text-secondary hover:bg-surface-overlay'
                }`}
              >
                Refund Due
              </button>
              <button
                type="button"
                onClick={() => setOutcomeType('demand')}
                className={`py-2 px-3 text-xs font-medium rounded-lg border text-center transition ${
                  outcomeType === 'demand'
                    ? 'bg-red-100 text-red-700 border-red-500 dark:bg-red-900/30 dark:text-red-300'
                    : 'border-surface-overlay text-text-secondary hover:bg-surface-overlay'
                }`}
              >
                Tax Demand
              </button>
            </div>

            {outcomeType !== 'nil' && (
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">
                  {outcomeType === 'refund' ? 'Refund Amount (₹)' : 'Demand Amount (₹)'}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary text-sm">
                    ₹
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={outcomeAmount}
                    onChange={(e) => setOutcomeAmount(e.target.value)}
                    placeholder="0"
                    className="input input-bordered w-full pl-8 text-sm"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-surface-overlay">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn btn-ghost btn-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary btn-sm"
            >
              {isSubmitting ? 'Saving…' : 'Complete Filing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
